import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Optional, TemplateRef, ViewChild, Output, EventEmitter, Input } from '@angular/core';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { MenuItem } from 'primeng/api';
import { Observable, Subscription, map } from 'rxjs';
import { TagsService } from '../../services/tags.service';
import { TagModel } from '../../models/tag.model';
import { UsersService } from '../../../../Core/Users/services/users.service';
import moment from 'moment-timezone';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { TableColumnModel } from '../../../Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../Forms/enums/table-column-input-type.enum';
import { TextInputDialogComponent } from '../../../Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '../../../Dialogs/models/text-input-dialog.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { APP_BASE_HREF } from '@angular/common';
import { LinkModel } from '../../../Forms/models/link.model';
import { TopicsService } from '../../../../Monitor/Core/services/topics.service';
import { MentionSearchListItemModel } from '../../../../Monitor/Shared/models/mention-search-list-item.model';
import { TagGroupsService } from '../../services/tag-groups.service';
import { TagGroupModel } from '../../models/tag-group.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { AgentSourceIndexService } from '../../../../AutomationAgents/services/agent-source-index.service';
import { ManageSourceAgentsService } from '../../../../AutomationAgents/services/manage-source-agents.service';
import { MonitoringSeriesTypeEnum } from '../../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

export interface CommonRowFields {
    name: string;
    createdDate?: string;
    createdDateRaw?: number;
    createdByUserId?: number;
    createdByUser?: string;
    lastModifiedDate?: string;
    lastModifiedDateRaw?: number;
    lastModifiedByUserId?: number;
    lastModifiedByUser?: string;
    selected?: boolean;
}


interface TagRow  extends CommonRowFields {
    tag: TagModel;
    topics?: LinkModel[];
    /** Icon classes and tooltip for the marker shown when an automation agent watches this tag */
    liveAgentIcon?: string;
    liveAgentTooltip?: string;
}

interface TagGroupRow extends CommonRowFields {
    tagGroup: TagGroupModel;
    tagIds?: number[];
    /** Icon classes and tooltip for the marker shown when an automation agent watches this tag group */
    liveAgentIcon?: string;
    liveAgentTooltip?: string;
}

@Component({
    selector: 'agpr-manage-tags-dialog',
    templateUrl: './manage-tags-dialog.component.html',
    styleUrls: ['./manage-tags-dialog.component.scss'],
    standalone: false
})
export class ManageTagsDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewInit {
    public readonly buttonTypes = ButtonTypesEnum;
    private subscriptions: Subscription[] = [];
    public selectedTab: number = 0;

    public tagToTopicsMap: Map<number, MentionSearchListItemModel[]>;
    public hasSelectedTags: boolean = false;
    /** The row menus' Agents item, offered only to accounts that can have automation agents */
    public tagRowActions: MenuItem[] = [];
    public tagGroupRowActions: MenuItem[] = [];

    /** Automation agents live on each tag, keyed by AgentSourceIndexService.key */
    private liveAgentCounts: Map<string, number> = new Map<string, number>();
    public allTags: TagModel[] = [];
    public rowData: TagRow[] = [];

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    hideSlideOverlay: boolean = false;

    tagNameColumn: TableColumnModel = {
        type: TableColumnInputEnum.text,
        heading: 'Tags.manageDialog.column.tagName',
        field: 'name',
        iconField: 'liveAgentIcon',
        iconTooltipField: 'liveAgentTooltip',
        tooltipField: 'name',
        fieldMaxLength: 64,
        sortable: true,
        sortType: 'text',
        selectable: true,
        selectField: 'selected',
        cellStyleClass: 'agpr-manageTags-tagName',
        styleClass: 'agpr-manageTags-tagName',
        width: 190
    };
    tagColumnDefs: TableColumnModel[] = [
        this.tagNameColumn,
        {
            heading: 'Tags.manageDialog.column.createdUser',
            field: 'createdByUser',
            sortable: true,
            sortType: 'text',
            width: 120,
            tooltipField: 'createdByUser'
        },
        {
            heading: 'Tags.manageDialog.column.createdDate',
            field: 'createdDate',
            sortable: true,
            sortField: 'createdDateRaw'
        },
        {
            heading: 'Tags.manageDialog.column.modifiedUser',
            field: 'lastModifiedByUser',
            sortable: true,
            sortType: 'text',
            width: 150,
            tooltipField: 'lastModifiedByUser'
        },
        {
            heading: 'Tags.manageDialog.column.modifiedDate',
            field: 'lastModifiedDate',
            sortable: true,
            sortField: 'lastModifiedDateRaw'
        }
    ];

    tagsTopicColumnDef: TableColumnModel = {
        heading: 'Tags.taggingTopics',
        field: 'topics',
        sortable: false,
        type: TableColumnInputEnum.linkList,
        message: 'Tags.numberOfTopicsSelected',
        listAriaLabel: 'Tags.manageDialog.topicList',
        width: 180
    };

    // Tag Groups
    public hasSelectedTagGroups: boolean = false;
    public allTagGroups: TagGroupModel[] = [];
    public tagGroupRowData: TagGroupRow[] = [];

    tagGroupNameColumn: TableColumnModel = {
        type: TableColumnInputEnum.text,
        heading: 'Tags.manageDialog.column.tagGroupName',
        field: 'name',
        iconField: 'liveAgentIcon',
        iconTooltipField: 'liveAgentTooltip',
        tooltipField: 'name',
        fieldMaxLength: 64,
        sortable: true,
        sortType: 'text',
        selectable: true,
        selectField: 'selected',
        cellStyleClass: 'agpr-manageTags-tableName',
        styleClass: 'agpr-manageTags-tableName',
        width: 200
    };

    tagIdsColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        field: 'tagIds',
        sortable: false,
        heading: 'Tags.manageDialog.column.tagGroupTags',
        customTemplate: null
    };

    tagGroupColumnDefs: TableColumnModel[] = [];

    @ViewChild('tagCell') public tagCellRef: TemplateRef<any>;

    constructor(
        public coreDialogService: CoreDialogService,
        public tagsService: TagsService,
        public usersService: UsersService,
        @Optional() public topicsService: TopicsService,
        public tagGroupsService: TagGroupsService,
        private agentSourceIndexService: AgentSourceIndexService,
        private currentUserStateService: CurrentUserStateService,
        private localizationService: LocalizationService,
        private manageSourceAgentsService: ManageSourceAgentsService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        super(coreDialogService, 'ManageTags');
    }

    public ngOnInit() {
        this.subscriptions.push(
            // Tags an automation agent is watching, marked in the name column and behind the row action
            this.agentSourceIndexService.liveAgentCounts$.subscribe(counts => {
                this.liveAgentCounts = counts;
                this.markLiveAgentTags();
            }),
            this.currentUserStateService.getAgenticFeatureEnabled().subscribe(enabled => {
                this.tagRowActions = enabled ? [this.agentsAction(row => this.addLiveAgent(row))] : [];
                this.tagGroupRowActions = enabled ? [this.agentsAction(row => this.addTagGroupLiveAgent(row))] : [];
            }),
            this.tagsService.all$.subscribe((tags) => {
                if (!tags) {
                    this.allTags = [];
                    this.rowData = [];
                    this.hasSelectedTags = false;

                    return;
                }
                // TODO keep selected items when list changes
                this.allTags = tags;
                this.setData();
            }),
            this.tagGroupsService.getTagGroupsList().subscribe((tagGroups) => {
                if (!tagGroups) {
                    this.allTagGroups = [];
                    this.tagGroupRowData = [];
                    this.hasSelectedTagGroups = false;

                    return;
                }
                this.allTagGroups = tagGroups;
                this.setData();
            })
        );


        if (this.topicsService) {
            this.tagColumnDefs.splice(1, 0, this.tagsTopicColumnDef);
            this.subscriptions.push(
                this.topicsService.getTagToTopics()
                    .pipe(map(topicMap => topicMap ?? new Map()))
                    .subscribe(tagToTopics => {
                        this.tagToTopicsMap = tagToTopics;
                        this.setData();
                    })
            );
        }

    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.tagIdsColumn.customTemplate = this.tagCellRef;
            this.tagGroupColumnDefs = [
                this.tagGroupNameColumn,
                this.tagIdsColumn
            ];
        });
    }

    /**
     * Clean up the subscription(s)
     */
    public ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    /**
     * Closes the dialog
     */
    public cancel() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    /**
     * Gets the specified list of users, and updates the row data with their names
     */
    private getUsers(userIds): void {
        this.usersService.getUsers(userIds).subscribe(
            (users: UserModel[]) => {
                const userMap = {};
                users.forEach((user) => {
                    userMap[user.id] = user;
                });
                this.rowData.forEach((row) => {
                    const createdUser = userMap[row.createdByUserId];
                    if (createdUser) {
                        row.createdByUser = createdUser.getDisplayName();
                    }
                    if (row.lastModifiedByUserId) {
                        const modifiedUser = userMap[row.lastModifiedByUserId];
                        if (modifiedUser) {
                            row.lastModifiedByUser = modifiedUser.getDisplayName();
                        }
                    }
                });
                this.rowData = this.rowData.slice();
                this.tagGroupRowData.forEach((tagGroupRow) => {
                    const createdUser = userMap[tagGroupRow.createdByUserId];
                    if (createdUser) {
                        tagGroupRow.createdByUser = createdUser.getDisplayName();
                    }
                    if (tagGroupRow.lastModifiedByUserId) {
                        const modifiedUser = userMap[tagGroupRow.lastModifiedByUserId];
                        if (modifiedUser) {
                            tagGroupRow.lastModifiedByUser = modifiedUser.getDisplayName();
                        }
                    }
                });
                this.tagGroupRowData = this.tagGroupRowData.slice();
            }
        );
    }

    /**
     * Checks to see if any rows have been selected
     */
    public checkForSelected(): void {
        this.hasSelectedTags = !!this.rowData.find(row => row.selected);
        this.hasSelectedTagGroups = !!this.tagGroupRowData.find(row => row.selected);
    }

    /**
     * Saves a tag when the table reports a change
     */
    public saveTag(tagRow: TagRow): void {
        let changed = false;
        const tag = tagRow.tag;

        if (tagRow.name !== tag.name) {
            tag.name = tagRow.name;
            changed = true;
        }

        if (changed) {
            this.tagsService.updateTag(tag).subscribe();
        }
    }

    /**
     * Verifies that the user intended to delete a set of tags before sending the delete request
     */
    public confirmDelete(tags: TagModel[]) {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'manageTagsDialog.confirmDialog',
                heading: 'Tags.manageDialog.confirmDelete.heading',
                alertMessage: 'Tags.manageDialog.confirmDelete.alert',
                alertType: messageSeverityEnum.warning,
                message: 'Tags.manageDialog.confirmDelete.message',
                list: tags.map(tag => tag.name),
                okLabel: 'Tags.manageDialog.delete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.tagsService.deleteTags(tags).subscribe();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    /**
     * Deletes all of the selected rows
     */
    public deleteSelected(): void {
        const selectedRows = this.rowData.filter(row => row.selected);
        const selectedTags = selectedRows.map(row => row.tag);

        this.deleteTags(selectedTags);
    }

    /**
     * Deletes tags if they aren't used in auto tagging
     */
    private deleteTags(tags: TagModel[]) {
        const tagsWithDependancies = [];

        const tagsToDelete = tags.filter(tag => {
            const topics = this.tagToTopicsMap.get(tag.id);

            if (topics && topics.length) {
                tagsWithDependancies.push(tag);
                return false;
            }

            return true;
        });

        if (tagsWithDependancies.length > 0) {
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Tags.manageDialog.cannotDeleteHeading',
                    showCancel: false,
                    alertType: messageSeverityEnum.warning,
                    alertMessage: 'Tags.manageDialog.cannotDeleteMessage',
                    list: tagsWithDependancies.map(tag => tag.name)
                },
                outputs: {
                    dialogClose: (confirm) => {
                        if (confirm) {
                            this.tagsService.deleteTags(tagsWithDependancies).subscribe();
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        } else {
            this.confirmDelete(tagsToDelete);
        }
    }

    /**
     * Deletes an individual row
     */
    public deleteTag(tagRow: TagRow): void {
        const tag = tagRow.tag;
        this.deleteTags([tag]);
    }

    /**
     * Verifies that a new tag name is valid
     */
    public getNewTagNameErrors(tagName: string): string[] {
        const errors = [];

        if (!tagName.trim()) {
            errors.push('Tags.manageDialog.newTag.error.emptyName');
        }

        if (this.allTags.find(tag => tag.name === tagName)) {
            errors.push('Tags.manageDialog.newTag.error.duplicateName');
        }

        return errors;
    }

    /**
     * Opens the create tag dialog that gets a new tag name from the user
     */
    public createTag(): void {
        const inputConfig: TextInputDialogModel = {
            inputs: {
                width: 280, // so it doesn't get wider when an error shows up
                heading: 'Tags.manageDialog.newTag.heading',
                description: 'Tags.manageDialog.newTag.description',
                okLabel: 'Tags.manageDialog.newTag.okLabel',
                placeholder: 'Tags.manageDialog.newTag.placeholder',
                disabledButtonTooltip:  'Tags.manageDialog.newTagGroup.disabledTooltip',
                formValidator: (text: string) => this.getNewTagNameErrors(text),
                submit: (text: string) => {
                    const tag = new TagModel();
                    tag.name = text;

                    return this.tagsService.createTag(tag);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, inputConfig);
    }

    public view(event: { tag?: TagModel; tagGroup?: TagGroupModel }) {
        const model = new MentionSearchCriteriaModel();

        if (event.tag) {
            model.query.tag.include = [ event.tag.id ];
        } else if (event.tagGroup) {
            model.query.tagGroup.include = [ event.tagGroup.id ];
        } else {
            return; // shouldn't happen
        }

        model.query.dateQuery = DateQueryModel.getAllQuery();

        const queryString = MentionSearchCriteriaModel.toFilterQueryString(model);

        window.open(`${this.appBaseHref}Monitoring/mentions/saved?filters=${queryString}`);
    }

    public tabChange(event: number): void {
        // 0 - tags
        // 1 - tagGroups
        if (event === 0) {
            this.tagGroupRowData.forEach(tagGroupRow => {
                tagGroupRow.selected = false;
            });
            this.hasSelectedTagGroups = false;
        } else if (event === 1) {
            this.rowData.forEach(tagRow => {
                tagRow.selected = false;
            });
            this.hasSelectedTags = false;
        }

        this.selectedTab = event;
    }

    /**
     * Tag groups
     */

    /**
     * Saves a tag group when the table reports a change
     */
    public saveTagGroup(tagGroupRow: TagGroupRow): void {
        let changed = false;
        const tagGroup = tagGroupRow.tagGroup;

        if (tagGroupRow.name !== tagGroup.name) {
            tagGroup.name = tagGroupRow.name;
            changed = true;
        }

        if (tagGroupRow.tagIds.length !== tagGroup.tagIds.length) {
            tagGroup.tagIds = tagGroupRow.tagIds;
            changed = true;
        } else {
            for (let i = 0; i < tagGroup.tagIds.length; i++) {
                if (tagGroup.tagIds[i] !== tagGroupRow.tagIds[i]) {
                    tagGroup.tagIds = tagGroupRow.tagIds;
                    changed = true;
                    break;
                }
            }
        }

        if (changed) {
            this.tagGroupsService.saveTagGroup(tagGroup);
        }
    }


    /**
     * Deletes tag groups
     */
    private deleteTagGroups(tagGroups: TagGroupModel[]) {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'manageTagsDialog.confirmDialog',
                heading: 'Tags.manageDialog.confirmDelete.tagGroup.heading',
                alertMessage: 'Tags.manageDialog.confirmDelete.tagGroup.alert',
                alertType: messageSeverityEnum.warning,
                message: 'Tags.manageDialog.confirmDelete.tagGroup.message',
                list: tagGroups.map(tag => tag.name),
                okLabel: 'Tags.manageDialog.delete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.tagGroupsService.deleteTagGroups(tagGroups);
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    /**
     * Deletes all of the selected tag group rows
     */
    public deleteSelectedTagGroups(): void {
        const selectedRows = this.tagGroupRowData.filter(row => row.selected);
        const selectedTagGroups = selectedRows.map(row => row.tagGroup);

        this.deleteTagGroups(selectedTagGroups);
    }

    /**
     * Deletes an individual tag group row
     */
    public deleteTagGroup(tagGroupRow: TagGroupRow): void {
        const tagGroup = tagGroupRow.tagGroup;
        this.deleteTagGroups([tagGroup]);
    }


    /**
     * Opens the create tag group dialog that gets a new tag group name from the user
     */
    public createTagGroup(): void {
        const inputConfig: TextInputDialogModel = {
            inputs: {
                width: 280, // so it doesn't get wider when an error shows up
                heading: 'Tags.manageDialog.newTagGroup.heading',
                description: 'Tags.manageDialog.newTagGroup.description',
                okLabel: 'Tags.manageDialog.newTagGroup.okLabel',
                placeholder: 'Tags.manageDialog.newTagGroup.placeholder',
                disabledButtonTooltip:  'Tags.manageDialog.newTagGroup.disabledTooltip',
                formValidator: (text: string) => this.getNewTagGroupNameErrors(text),
                submit: (text: string) => {
                    const tagGroup = new TagGroupModel();
                    tagGroup.name = text;

                    return this.tagGroupsService.createTagGroup(tagGroup);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, inputConfig);
    }

    /**
     * Verifies that a new tag group name is valid
     */
    public getNewTagGroupNameErrors(tagGroupName: string): string[] {
        const errors = [];

        if (!tagGroupName.trim()) {
            errors.push('Tags.manageDialog.newTagGroup.error.emptyName');
        }

        if (this.allTagGroups.find(tagGroup => tagGroup.name === tagGroupName)) {
            errors.push('Tags.manageDialog.newTagGroup.error.duplicateName');
        }

        return errors;
    }

    /**
     * Called by the template when a row changes
     */
    public tagGroupTagsUpdated(event: number[], tagGroupRow: TagGroupRow): void {
        tagGroupRow.tagIds = event;
        this.saveTagGroup(tagGroupRow);
    }

    /** Opens the panel that manages the automation agents watching this tag */
    public addLiveAgent(row: TagRow): void {
        this.manageSourceAgentsService.open(MonitoringSeriesTypeEnum.tag, row?.tag?.id);
    }

    /** Opens the panel that manages the automation agents watching this tag group */
    public addTagGroupLiveAgent(row: TagGroupRow): void {
        this.manageSourceAgentsService.open(MonitoringSeriesTypeEnum.tagGroup, row?.tagGroup?.id);
    }

    private agentsAction(open: (row: any) => void): MenuItem {
        return {
            label: 'Monitoring.result.agents',
            icon: 'fa-kit-duotone fa-agpr-ai-generic',
            command: (event: any) => open(event.data)
        };
    }

    /** Stamps the rows an automation agent is live on, so the name columns can mark them */
    private markLiveAgentTags(): void {
        this.rowData.forEach(row => this.markLiveAgentRow(row, MonitoringSeriesTypeEnum.tag, row.tag?.id));
        this.tagGroupRowData.forEach(row => this.markLiveAgentRow(row, MonitoringSeriesTypeEnum.tagGroup, row.tagGroup?.id));
    }

    private markLiveAgentRow(row: TagRow | TagGroupRow, type: MonitoringSeriesTypeEnum, id: number): void {
        const count = this.liveAgentCounts.get(this.agentSourceIndexService.key(type, id)) ?? 0;

        row.liveAgentIcon = count ? 'fa-solid fa-signal-stream' : null;
        row.liveAgentTooltip = count
            ? this.localizationService.transform('AutomationAgents.liveAgent', { count: `${count}` })
            : null;
    }

    /**
     * Sets rowData with available data
     */
    private setData() {
        if (this.topicsService && !this.tagToTopicsMap) {
            // topics not loaded yet
            return;
        }

        const userIds = [];
        this.rowData = this.allTags.map((tag) => {
            const row: TagRow = {
                tag: tag,
                name: tag.name,
                createdDate: moment(tag.createdDate).format('MMMM D, YYYY'),
                createdDateRaw: tag.createdDate,
                createdByUserId: tag.createdByUserId
            };

            userIds.push(tag.createdByUserId);
            if (tag.lastModifiedByUserId) {
                userIds.push(tag.lastModifiedByUserId);
                row.lastModifiedDateRaw = tag.lastModifiedDate;
                row.lastModifiedDate = moment(tag.lastModifiedDate).format('MMMM D, YYYY');
                row.lastModifiedByUserId = tag.lastModifiedByUserId;
            } else {
                row.lastModifiedDate = null;
                row.lastModifiedByUser = null;
            }

            let taggingTopics: MentionSearchListItemModel[];
            let topicLinks = [];

            if (this.tagToTopicsMap) {
                taggingTopics = this.tagToTopicsMap.get(tag.id);
            }

            if (taggingTopics) {
                topicLinks = taggingTopics.map(topic => {
                    return {
                        label: topic.name,
                        url: `${this.appBaseHref}Monitoring/topics/topic/${topic.id}/update`
                    };
                });

                row.topics = topicLinks;
            }

            return row;
        });
        this.tagGroupRowData = this.allTagGroups.map((tagGroup) => {
            const row: TagGroupRow = {
                tagGroup: tagGroup,
                name: tagGroup.name,
                createdDate: moment(tagGroup.createdDate).format('MMMM D, YYYY'),
                createdDateRaw: tagGroup.createdDate,
                createdByUserId: tagGroup.createdByUserId
            };

            userIds.push(tagGroup.createdByUserId);
            if (tagGroup.lastModifiedByUserId) {
                userIds.push(tagGroup.lastModifiedByUserId);
                row.lastModifiedDateRaw = tagGroup.lastModifiedDate;
                row.lastModifiedDate = moment(tagGroup.lastModifiedDate).format('MMMM D, YYYY');
                row.lastModifiedByUserId = tagGroup.lastModifiedByUserId;
            } else {
                row.lastModifiedDate = null;
                row.lastModifiedByUser = null;
            }

            row.tagIds = tagGroup.tagIds.slice();

            return row;
        });
        this.markLiveAgentTags();
        this.checkForSelected();
        this.getUsers(userIds);
    }
}
